import type { FormElement } from '../../model/form.types';
import type { ColumnDef, FieldValue, TextStyle, ValidationIssue } from '../../model/types';

export const TYPE_MESSAGE = 'Valor com formato inválido.';
export const SINGLE_SELECTION_MESSAGE = 'Selecione apenas uma opção.';

export function textStyle(element: FormElement): TextStyle {
  return {
    fontSize: element.properties.fontSize,
    fontWeight: element.properties.fontWeight,
    fontStyle: element.properties.fontStyle,
    textDecoration: element.properties.textDecoration,
    textAlign: element.properties.textAlign,
    color: element.properties.color,
    backgroundColor: element.properties.backgroundColor,
  };
}

export function fieldLabel(element: FormElement): string {
  const label = element.properties.label?.trim();
  return label && label.length > 0 ? label : element.properties.name;
}

// Booleans and numbers are never "empty": a `false` or `0` stored in a selection field must
// reach the type check instead of passing as a blank answer.
export function isEmptyValue(value: FieldValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

// The builder lets the author type a default ("Valor") that the field shows before anyone answers.
// It lives in the matrix, not in the answers, so the browser and the server derive the same initial
// value, and an answer the person cleared (stored as '') still wins over it.
export function builderDefaultText(element: FormElement): string {
  const value: unknown = element.properties.value;
  return typeof value === 'string' && value.trim().length > 0 ? value : '';
}

export function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

export function requiredIssue(element: FormElement): ValidationIssue {
  return { fieldId: element.id, code: 'required', message: 'Campo obrigatório.' };
}

export function typeIssue(element: FormElement, message: string = TYPE_MESSAGE): ValidationIssue {
  return { fieldId: element.id, code: 'type', message };
}

export function stringShapeIssue(element: FormElement, value: FieldValue): ValidationIssue | null {
  return value === null || typeof value === 'string' ? null : typeIssue(element);
}

export function hasSelectionShape(value: FieldValue): boolean {
  return value === null || typeof value === 'string' || isStringList(value);
}

export function selectedStrings(value: FieldValue): string[] {
  if (isStringList(value)) return value;
  if (typeof value === 'string' && value.length > 0) return [value];
  return [];
}

// Cardinality is part of the shape: a single selection field holding two options cannot be
// read as one answer, so the issue is raised even while the field is hidden by a rule.
export function selectionShapeIssue(
  element: FormElement,
  value: FieldValue,
  multiple: boolean,
): ValidationIssue | null {
  if (!hasSelectionShape(value)) return typeIssue(element);
  if (!multiple && selectedStrings(value).length > 1) return typeIssue(element, SINGLE_SELECTION_MESSAGE);
  return null;
}

export function maxLengthIssue(element: FormElement, maxLength: number): ValidationIssue {
  return { fieldId: element.id, code: 'maxLength', message: `Máximo de ${maxLength} caracteres.` };
}

export function formatIssue(element: FormElement, message: string): ValidationIssue {
  return { fieldId: element.id, code: 'regex', message };
}

export function validateFormattedText(
  element: FormElement,
  value: FieldValue,
  accepts: (text: string) => boolean,
  message: string,
): ValidationIssue[] {
  const shape = stringShapeIssue(element, value);
  if (shape) return [shape];
  if (typeof value !== 'string' || isEmptyValue(value)) {
    return element.properties.required ? [requiredIssue(element)] : [];
  }
  return accepts(value) ? [] : [formatIssue(element, message)];
}

export const OPTION_MESSAGE = 'Opção fora da lista da matriz.';

export function optionIssue(element: FormElement): ValidationIssue {
  return { fieldId: element.id, code: 'option', message: OPTION_MESSAGE };
}

export function validateSelection(
  element: FormElement,
  value: FieldValue,
  known: string[],
  multiple: boolean,
): ValidationIssue[] {
  if (!hasSelectionShape(value)) return [typeIssue(element)];
  if (isEmptyValue(value)) return element.properties.required ? [requiredIssue(element)] : [];
  const issues: ValidationIssue[] = [];
  const cardinality = selectionShapeIssue(element, value, multiple);
  if (cardinality) issues.push(cardinality);
  const knownSet = new Set(known);
  if (selectedStrings(value).some((item) => !knownSet.has(item))) issues.push(optionIssue(element));
  return issues;
}

export function singleColumn(element: FormElement, label: string): ColumnDef[] {
  return [{ key: element.id, fieldId: element.id, label }];
}

export function valueAsText(value: FieldValue): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
}

export function normalizeOption(option: unknown): string {
  return typeof option === 'string' ? option.trim() : '';
}
